import React, { useEffect, useState, useRef } from "react";
import {
  clearProductTimeout,
  setProductTimeout,
  type ProductTimer,
} from "@/utils/productTimers";

const ToolTip2 = () => {
  const [tooltipText, setTooltipText] = useState("");
  const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({
    top: 0,
    left: 0,
    bottom: 0,
  });
  const [showAbove, setShowAbove] = useState(false);
  const timeoutRef = useRef<ProductTimer | null>(null);

  const hideTooltip = () => {
    setTooltipAnchor(null);
    setTooltipText("");
    if (timeoutRef.current) {
      clearProductTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleMouseEnter = (
    event: React.MouseEvent<HTMLElement>,
    text: string
  ) => {
    setTooltipAnchor(event.currentTarget as HTMLElement);
    setTooltipText(text);

    const shouldShowAbove =
      event.currentTarget.getBoundingClientRect().bottom + 50 >
      window.innerHeight;
    setShowAbove(shouldShowAbove);
    setTooltipPosition({
      top: event.currentTarget.getBoundingClientRect().bottom + 10,
      bottom:
        window.innerHeight -
        event.currentTarget.getBoundingClientRect().top +
        10,
      left: event.currentTarget.getBoundingClientRect().left,
    });

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearProductTimeout(timeoutRef.current);
    }

    // Set a fallback timeout to hide tooltip after 10 seconds
    timeoutRef.current = setProductTimeout(() => {
      hideTooltip();
    }, 10000);
  };

  const handleMouseLeave = () => {
    hideTooltip();
  };

  // Global mouse move listener to detect when mouse moves outside tooltip area
  const handleGlobalMouseMove = (event: MouseEvent) => {
    if (!tooltipAnchor) return;

    // Check if mouse is still within the tooltip anchor element
    const rect = tooltipAnchor.getBoundingClientRect();
    const isWithinAnchor =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    if (!isWithinAnchor) {
      hideTooltip();
    }
  };

  // Handle visibility change (when page becomes hidden/visible)
  const handleVisibilityChange = () => {
    if (document.hidden) {
      hideTooltip();
    }
  };

  // Handle page unload
  const handlePageUnload = () => {
    hideTooltip();
  };

  useEffect(() => {
    (window as Window).showTooltip = handleMouseEnter;
    (window as Window).hideTooltip = handleMouseLeave;

    // Add global event listeners
    document.addEventListener("mousemove", handleGlobalMouseMove);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handlePageUnload);
    window.addEventListener("pagehide", handlePageUnload);

    // Cleanup function
    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handlePageUnload);
      window.removeEventListener("pagehide", handlePageUnload);

      if (timeoutRef.current) {
        clearProductTimeout(timeoutRef.current);
      }
    };
  }, [tooltipAnchor]);

  return tooltipAnchor ? (
    <div
      style={{
        position: "absolute",
        ...(showAbove
          ? { bottom: `${tooltipPosition.bottom}px` }
          : { top: `${tooltipPosition.top}px` }),
        left: `${tooltipPosition.left}px`,
        zIndex: 1400,
        fontFamily: "monospace",
      }}
      className="bg-gray-600 text-[12px] text-white p-2 rounded-md border shadow-xl"
    >
      {tooltipText}
    </div>
  ) : null;
};

export default ToolTip2;
